import { MinusCircle } from "lucide-react";
/**
 * New punch item / New observation — the records table's create sheet
 * (`Led AI - Records Table.dc.html`, "New record").
 *
 * A title, a description, photos, and a row of property chips — status,
 * priority, assignee, company, location, due date, and the record's trade
 * (punch) or type (observation). The directories behind the chips are the
 * project's own, read through the same routes the record page's editor uses
 * (`/api/item-edit-options`, `/api/item-assignees`), so a person, a location
 * or a trade picked here is a Procore identity and syncs as one.
 *
 * ## Send to the assignee
 *
 * A punch item with an assignee is SENT by default: once it exists in
 * Procore, the backend calls Procore's send step, Procore emails the
 * assignee, and the item moves Initiated → Work Required. The status chip
 * says which of the two it will be before you press Create; the toggle turns
 * the send off for an item you want to hold.
 *
 * ## Company
 *
 * Procore puts the company on the PERSON, not on the punch item, so Company
 * here narrows the assignee picker to that sub's people and fills itself in
 * from whoever is picked. It is never sent on its own.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  fetchItemAssignees,
  fetchItemEditOptions,
  newCreateOperationId,
  postItemCreate,
  type ConsoleSite,
  type ItemCreateResult,
  type ItemEditDirectoryOption,
  type ItemEditOptions,
} from "../../api";
import { isoDay, parseDay } from "../../components/Calendar";
import { DsIcon } from "../../components/DsIcon";
import { PROCORE_MARK_URL } from "../../assets/procoreMark";
import { DS, FONT_MONO, FONT_TEXT, RT } from "../../tokens";
import { PRIORITY_LABEL, PUNCH_STATUSES, OBS_STATUSES, shortDay, type Priority } from "./recordModel";
import { CompanySwatch, PersonAvatar, PriorityBars, StatusGlyph, Toggle } from "./parts";

export interface CreatedRecord {
  itemId: string;
  assignee?: string;
  sync: ItemCreateResult["sync"];
}

interface Pick {
  remoteId?: string;
  name: string;
  /** The assignee's company, carried so Company can fill itself in. */
  company?: string;
}

interface Draft {
  title: string;
  description: string;
  priority: Priority;
  assignee: Pick | null;
  company: string | null;
  location: Pick | null;
  due: string;
  trade: Pick | null;
  type: string | null;
  photos: Array<{ base64Data: string; mimeType: string; preview: string; name: string }>;
}

const BLANK: Draft = {
  title: "",
  description: "",
  priority: "medium",
  assignee: null,
  company: null,
  location: null,
  due: "",
  trade: null,
  type: null,
  photos: [],
};

const MAX_PHOTOS = 6;
/** The backend's per-photo ceiling (`checkRpcMediaPayload`), decoded. */
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

type Field = "project" | "priority" | "assignee" | "company" | "location" | "due" | "trade" | "type";

export function NewRecordModal({
  vocab,
  sites,
  activeSiteId,
  search,
  onClose,
  onCreated,
}: {
  vocab: "punch" | "obs";
  /** Projects a record can be created on — never an Autodesk one. */
  sites: ConsoleSite[];
  activeSiteId: string | null;
  search: string;
  onClose: () => void;
  onCreated: (created: CreatedRecord) => void;
}) {
  const isPunch = vocab === "punch";
  const [siteId, setSiteId] = useState<string | null>(
    () => (activeSiteId && sites.some((site) => site.id === activeSiteId) ? activeSiteId : sites.find((site) => site.syncSource === "procore")?.id ?? sites[0]?.id ?? null),
  );
  const site = sites.find((entry) => entry.id === siteId) ?? null;
  const procore = site?.syncSource === "procore";
  const [draft, setDraft] = useState<Draft>(BLANK);
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const [openField, setOpenField] = useState<Field | null>(null);
  const [query, setQuery] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [createMore, setCreateMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operationId = useRef(newCreateOperationId());
  const fileInput = useRef<HTMLInputElement>(null);
  const titleInput = useRef<HTMLInputElement>(null);

  // ---- directories ------------------------------------------------------
  const category = isPunch ? "punch" : "observations";
  const [options, setOptions] = useState<ItemEditOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let live = true;
    setOptions(null);
    setOptionsError(null);
    fetchItemEditOptions(siteId, category, search)
      .then((result) => live && setOptions(result))
      .catch((failure: Error) => live && setOptionsError(failure.message || "Project directories are unavailable."));
    return () => { live = false; };
  }, [siteId, category, search]);

  const [people, setPeople] = useState<ItemEditDirectoryOption[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  useEffect(() => {
    if (!siteId || (openField !== "assignee" && openField !== "company")) return;
    let live = true;
    const term = openField === "assignee" ? query.trim() : "";
    setPeopleLoading(true);
    const timer = setTimeout(() => {
      fetchItemAssignees(siteId, category, term, search)
        .then((result) => { if (live) { setPeople(result); setPeopleError(null); } })
        .catch((failure: Error) => { if (live) { setPeople([]); setPeopleError(failure.message || "The project's people are unavailable."); } })
        .finally(() => live && setPeopleLoading(false));
    }, term ? 220 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [siteId, category, search, openField, query]);

  // A new project is a new directory: anything picked from the old one goes.
  useEffect(() => {
    set({ assignee: null, company: null, location: null, trade: null, type: null });
  }, [siteId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (openField) setOpenField(null);
        else onClose();
      }
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) void create();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const toggle = (field: Field) => {
    setQuery("");
    setOpenField((current) => (current === field ? null : field));
  };

  // ---- status, and what will happen ---------------------------------------
  const willSend = isPunch && procore && !!draft.assignee?.remoteId && sendNow;
  const initial = isPunch
    ? PUNCH_STATUSES.find((s) => s.key === (willSend ? "work_required" : "initiated"))!
    : OBS_STATUSES[0]!;
  const firstName = draft.assignee?.name.split(/\s+/)[0];
  const syncNote = !site
    ? "Pick a project"
    : !procore
      ? "Saved in Led AI · this project has no Procore link"
      : isPunch
        ? draft.assignee
          ? draft.assignee.remoteId
            ? sendNow
              ? `Work Required · Procore emails ${draft.assignee.name}`
              : "Initiated · nothing sent yet"
            : "Initiated · this person is not in Procore, so nothing is sent"
          : "Initiated · add an assignee to send"
        : "Creates in Procore as Initiated";

  // ---- photos -------------------------------------------------------------
  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    const room = MAX_PHOTOS - draft.photos.length;
    const chosen = [...files].filter((file) => file.type.startsWith("image/")).slice(0, room);
    const tooBig = chosen.filter((file) => file.size > MAX_PHOTO_BYTES);
    if (tooBig.length) setError(`${tooBig.map((file) => file.name).join(", ")} ${tooBig.length === 1 ? "is" : "are"} over 8 MB`);
    const read = await Promise.all(
      chosen.filter((file) => file.size <= MAX_PHOTO_BYTES).map(
        (file) =>
          new Promise<Draft["photos"][number] | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const url = String(reader.result ?? "");
              const comma = url.indexOf(",");
              resolve(comma > 0 ? { base64Data: url.slice(comma + 1), mimeType: file.type, preview: url, name: file.name } : null);
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          }),
      ),
    );
    setDraft((current) => ({ ...current, photos: [...current.photos, ...read.filter((photo): photo is NonNullable<typeof photo> => !!photo)].slice(0, MAX_PHOTOS) }));
  };

  // ---- create -------------------------------------------------------------
  const canCreate = !!draft.title.trim() && !!site && !busy;
  async function create() {
    if (!canCreate || !site) return;
    setBusy(true);
    setError(null);
    const result = await postItemCreate(isPunch ? "punch" : "observation", search, {
      jobSiteId: site.id,
      title: draft.title.trim(),
      ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
      priority: draft.priority,
      ...(draft.due ? { dueDate: draft.due } : {}),
      ...(draft.assignee?.remoteId ? { selectedAssigneeRemoteId: draft.assignee.remoteId } : {}),
      ...(draft.assignee ? { selectedAssigneeName: draft.assignee.name } : {}),
      ...(draft.location?.remoteId ? { selectedLocationRemoteId: draft.location.remoteId } : {}),
      ...(draft.location ? { selectedLocationName: draft.location.name } : {}),
      ...(isPunch && draft.trade?.remoteId ? { selectedTradeRemoteId: draft.trade.remoteId } : {}),
      ...(isPunch && draft.trade ? { selectedTradeName: draft.trade.name } : {}),
      ...(!isPunch && draft.type ? { observationType: draft.type } : {}),
      ...(isPunch ? { sendToAssignee: sendNow } : {}),
      ...(draft.photos.length ? { photos: draft.photos.map(({ base64Data, mimeType }) => ({ base64Data, mimeType })) } : {}),
      operationId: operationId.current,
    });
    setBusy(false);
    if (!result.ok || !result.result) {
      setError(result.error || "Could not create it — try again.");
      return;
    }
    onCreated({ itemId: result.result.itemId, assignee: draft.assignee?.name, sync: result.result.sync });
    operationId.current = newCreateOperationId();
    if (createMore) {
      setDraft({ ...BLANK, priority: draft.priority });
      setOpenField(null);
      titleInput.current?.focus();
    } else {
      onClose();
    }
  }

  // ---- menus --------------------------------------------------------------
  const q = query.trim().toLowerCase();
  const matches = (label: string) => !q || label.toLowerCase().includes(q);
  const peopleShown = people.filter((person) => !draft.company || person.company === draft.company);
  const companies = useMemo(
    () => [...new Set(people.map((person) => person.company).filter((name): name is string => !!name))].sort((a, b) => a.localeCompare(b)),
    [people],
  );
  const dueQuick = useMemo(() => {
    const today = new Date();
    return ([["Tomorrow", 1], ["In 3 days", 3], ["Next week", 7]] as const).map(([label, days]) => ({ label, date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + days) }));
  }, []);
  const dueDate = draft.due ? parseDay(draft.due) : null;

  // Portaled to the body: the console pane is a containing block for
  // `position: fixed`, which would centre the sheet on the pane and leave the
  // sidebar and the Ask composer undimmed.
  return createPortal(
    <div
      data-testid="new-record-scrim"
      className="rt-fade"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: RT.scrim, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "72px 24px 24px", overflowY: "auto", fontFamily: FONT_TEXT }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isPunch ? "New punch item" : "New observation"}
        data-testid="new-record"
        data-screen-label="New record"
        onClick={(event) => { event.stopPropagation(); if (openField) setOpenField(null); }}
        style={{ width: 760, maxWidth: "100%", display: "flex", flexDirection: "column", background: RT.pane, borderRadius: 16, boxShadow: RT.shadowModal, color: DS.ink }}
      >
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px 0" }}>
          <span style={{ position: "relative", flex: "none" }}>
            <button
              type="button"
              data-testid="new-record-project"
              disabled={sites.length < 2}
              onClick={(event) => { event.stopPropagation(); toggle("project"); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 10px 0 8px", borderRadius: 13, border: "1px solid rgba(10,10,10,0.1)", background: RT.pane, fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: 1, color: RT.cell, whiteSpace: "nowrap", cursor: sites.length < 2 ? "default" : "pointer", textTransform: "uppercase", maxWidth: 280 }}
            >
              {procore ? <img src={PROCORE_MARK_URL} alt="" style={{ width: 11, height: 11, borderRadius: 2, display: "block" }} /> : <DsIcon name="pin" size={11} color={RT.cell} />}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{site?.name || "Choose a project"}</span>
              {sites.length > 1 && <DsIcon name="chevron" size={9} color={DS.meta} style={{ transform: "rotate(90deg)" }} />}
            </button>
            {openField === "project" && (
              <PickerMenu
                query={query}
                onQuery={setQuery}
                placeholder="Project…"
                empty={!sites.some((entry) => matches(entry.name))}
              >
                {sites.filter((entry) => matches(entry.name)).map((entry) => (
                  <PickerOption key={entry.id} on={entry.id === siteId} onPick={() => { setSiteId(entry.id); setOpenField(null); }}
                    icon={entry.syncSource === "procore" ? <img src={PROCORE_MARK_URL} alt="" style={{ width: 13, height: 13, borderRadius: 3, display: "block" }} /> : <DsIcon name="pin" size={13} color={DS.meta} />}
                  >
                    {entry.name || entry.id}
                  </PickerOption>
                ))}
              </PickerMenu>
            )}
          </span>
          <DsIcon name="chevron" size={11} color={DS.dashed} />
          <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>{isPunch ? "New punch item" : "New observation"}</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="rt-hover-ink"
            aria-label="Close"
            onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 15, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", cursor: "pointer", color: DS.body2 }}
          >
            <DsIcon name="close" size={14} color="currentColor" />
          </button>
        </div>

        {/* body */}
        <div style={{ padding: "14px 20px 0" }}>
          <input
            ref={titleInput}
            data-testid="new-record-title"
            className="rt-input"
            value={draft.title}
            onInput={(event) => set({ title: event.currentTarget.value })}
            placeholder={isPunch ? "What needs to be fixed?" : "What did you observe?"}
            aria-label="Title"
            autoFocus
            style={{ width: "100%", border: 0, outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px", color: DS.ink, padding: 0 }}
          />
          <textarea
            data-testid="new-record-description"
            className="rt-input"
            value={draft.description}
            onInput={(event) => set({ description: event.currentTarget.value })}
            placeholder="Add description…"
            aria-label="Description"
            rows={3}
            style={{ flex: "none", display: "block", width: "100%", minHeight: 84, marginTop: 10, resize: "none", border: 0, outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 14.5, lineHeight: 1.5, color: DS.ink, padding: 0 }}
          />
          {draft.photos.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {draft.photos.map((photo, index) => (
                <span key={index} data-testid="new-record-photo" style={{ position: "relative", width: 60, height: 80, borderRadius: 7, overflow: "hidden", background: RT.photoBg, display: "block" }}>
                  <img src={photo.preview} alt={photo.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button
                    type="button"
                    aria-label={`Remove ${photo.name}`}
                    onClick={() => set({ photos: draft.photos.filter((_, k) => k !== index) })}
                    style={{ position: "absolute", right: 3, top: 3, width: 18, height: 18, borderRadius: 9, background: "rgba(10,10,10,0.6)", border: "none", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  >
                    <MinusCircle size={14} color={RT.pane} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 16, paddingBottom: 14 }}>
            <span data-testid="new-record-status" title={isPunch ? "Where it starts in Procore's punch workflow" : undefined} style={{ ...CHIP, cursor: "default" }}>
              <StatusGlyph status={initial} size={13} />
              {initial.label}
            </span>

            <ChipPicker
              field="priority" openField={openField} onToggle={toggle} has
              shown={PRIORITY_LABEL[draft.priority]}
              icon={<PriorityBars priority={draft.priority} tall={11} />}
              query={query} onQuery={setQuery} placeholder="Priority…"
              empty={!(["high", "medium", "low"] as const).some((p) => matches(PRIORITY_LABEL[p]))}
            >
              {(["high", "medium", "low"] as const).filter((p) => matches(PRIORITY_LABEL[p])).map((p) => (
                <PickerOption key={p} on={draft.priority === p} onPick={() => { set({ priority: p }); setOpenField(null); }}
                  icon={<span style={{ width: 9, height: 9, borderRadius: 2, background: p === "low" ? RT.priority.lowSwatch : RT.priority[p], display: "block" }} />}
                >
                  {PRIORITY_LABEL[p]}
                </PickerOption>
              ))}
            </ChipPicker>

            <ChipPicker
              field="assignee" openField={openField} onToggle={toggle} has={!!draft.assignee}
              shown={draft.assignee?.name ?? "Select assignee"}
              icon={draft.assignee ? <span style={{ marginLeft: -3, display: "inline-flex" }}><PersonAvatar name={draft.assignee.name} size={18} /></span> : <DsIcon name="person" size={13} color="currentColor" />}
              query={query} onQuery={setQuery} placeholder={draft.company ? `People at ${draft.company}…` : "Assignee…"}
              empty={!peopleLoading && peopleShown.length === 0 && !(q && !procore)}
              loading={peopleLoading && peopleShown.length === 0}
              note={peopleError ?? undefined}
              clearable={!!draft.assignee} onClear={() => { set({ assignee: null }); setOpenField(null); }}
            >
              {peopleShown.map((person) => (
                <PickerOption key={person.remoteId} on={draft.assignee?.remoteId === person.remoteId}
                  onPick={() => { set({ assignee: { remoteId: person.remoteId, name: person.name, company: person.company }, company: person.company ?? draft.company }); setOpenField(null); }}
                  icon={<PersonAvatar name={person.name} size={18} />}
                  detail={person.company}
                >
                  {person.name}
                </PickerOption>
              ))}
              {/* An unlinked project has no directory to match against: the
                  typed name IS the assignee, as on the phone. */}
              {!procore && q && !peopleShown.some((person) => person.name.toLowerCase() === q) && (
                <PickerOption on={false} onPick={() => { set({ assignee: { name: query.trim() } }); setOpenField(null); }} icon={<DsIcon name="plus" size={12} color={DS.meta} />}>
                  {`Assign to “${query.trim()}”`}
                </PickerOption>
              )}
            </ChipPicker>

            <ChipPicker
              field="company" openField={openField} onToggle={toggle} has={!!draft.company}
              shown={draft.company ?? "Select company"}
              icon={draft.company ? <CompanySwatch name={draft.company} size={10} radius={3} /> : <DsIcon name="device" size={13} color="currentColor" />}
              query={query} onQuery={setQuery} placeholder="Company…"
              empty={!peopleLoading && !companies.some(matches)}
              loading={peopleLoading && companies.length === 0}
              note={peopleError ?? undefined}
              clearable={!!draft.company} onClear={() => { set({ company: null }); setOpenField(null); }}
            >
              {companies.filter(matches).map((name) => (
                <PickerOption key={name} on={draft.company === name}
                  onPick={() => { set({ company: name, assignee: draft.assignee?.company === name ? draft.assignee : null }); setOpenField(null); }}
                  icon={<CompanySwatch name={name} size={9} />}
                >
                  {name}
                </PickerOption>
              ))}
            </ChipPicker>

            <ChipPicker
              field="location" openField={openField} onToggle={toggle} has={!!draft.location}
              shown={draft.location?.name ?? "Select a location"}
              icon={<DsIcon name="pin" size={13} color="currentColor" />}
              query={query} onQuery={setQuery} placeholder="Location…"
              empty={!!options && !(options.locations ?? []).some((loc) => matches(loc.path ?? loc.name)) && !q}
              loading={!options && !optionsError}
              note={optionsError ?? undefined}
              clearable={!!draft.location} onClear={() => { set({ location: null }); setOpenField(null); }}
            >
              {(options?.locations ?? []).filter((loc) => matches(loc.path ?? loc.name)).slice(0, 80).map((loc) => (
                <PickerOption key={loc.remoteId} on={draft.location?.remoteId === loc.remoteId}
                  onPick={() => { set({ location: { remoteId: loc.remoteId, name: loc.path ?? loc.name } }); setOpenField(null); }}
                  icon={<DsIcon name="pin" size={12} color={DS.meta} />}
                >
                  {loc.path ?? loc.name}
                </PickerOption>
              ))}
              {q && !(options?.locations ?? []).some((loc) => (loc.path ?? loc.name).toLowerCase() === q) && (
                <PickerOption on={false} onPick={() => { set({ location: { name: query.trim() } }); setOpenField(null); }} icon={<DsIcon name="plus" size={12} color={DS.meta} />}>
                  {`Use “${query.trim()}”`}
                </PickerOption>
              )}
            </ChipPicker>

            <span style={{ position: "relative", flex: "none" }}>
              <button
                type="button"
                className="rt-hover"
                data-testid="new-record-due"
                aria-expanded={openField === "due"}
                onClick={(event) => { event.stopPropagation(); toggle("due"); }}
                style={{ ...CHIP, borderColor: openField === "due" ? DS.ink : "rgba(10,10,10,0.12)", background: draft.due ? RT.hover : RT.pane, color: draft.due ? DS.ink : DS.body2, cursor: "pointer" }}
              >
                <DsIcon name="calendar" size={13} color="currentColor" />
                {dueDate ? `${shortDay(dueDate)}, ${dueDate.getFullYear()}` : "Due date"}
              </button>
              {openField === "due" && (
                <div className="rt-fade" onClick={(event) => event.stopPropagation()} style={{ position: "absolute", left: 0, top: 36, zIndex: 60, width: 230, padding: 10, background: RT.pane, border: "1px solid rgba(10,10,10,0.12)", borderRadius: 10, boxShadow: RT.shadowPopover, display: "flex", flexDirection: "column", gap: 6 }}>
                  {dueQuick.map(({ label, date }) => (
                    <button
                      key={label}
                      type="button"
                      className="rt-hover"
                      onClick={() => { set({ due: isoDay(date) }); setOpenField(null); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 32, padding: "0 10px", borderRadius: 7, border: "none", background: "transparent", font: "inherit", fontSize: 13, color: DS.ink, cursor: "pointer" }}
                    >
                      <span>{label}</span>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: RT.count }}>{`${shortDay(date)}, ${date.getFullYear()}`}</span>
                    </button>
                  ))}
                  <input
                    type="date"
                    aria-label="Due date"
                    value={draft.due}
                    onChange={(event) => set({ due: event.target.value })}
                    style={{ height: 34, border: "1px solid rgba(10,10,10,0.12)", borderRadius: 8, padding: "0 10px", fontFamily: FONT_TEXT, fontSize: 13, color: DS.ink, outline: "none" }}
                  />
                  {draft.due && (
                    <button type="button" className="rt-hover" onClick={() => { set({ due: "" }); setOpenField(null); }} style={CLEAR_ROW}>
                      <DsIcon name="close" size={11} color={DS.meta} />
                      Clear
                    </button>
                  )}
                </div>
              )}
            </span>

            {isPunch ? (
              <ChipPicker
                field="trade" openField={openField} onToggle={toggle} has={!!draft.trade}
                shown={draft.trade?.name ?? "Select trade"}
                icon={<DsIcon name="list" size={13} color="currentColor" />}
                query={query} onQuery={setQuery} placeholder="Trade…"
                empty={!!options && !(options.trades ?? []).some((t) => matches(t.name)) && !q}
                loading={!options && !optionsError}
                clearable={!!draft.trade} onClear={() => { set({ trade: null }); setOpenField(null); }}
              >
                {(options?.trades ?? []).filter((t) => matches(t.name)).map((t) => (
                  <PickerOption key={t.remoteId} on={draft.trade?.remoteId === t.remoteId} onPick={() => { set({ trade: { remoteId: t.remoteId, name: t.name } }); setOpenField(null); }}>
                    {t.name}
                  </PickerOption>
                ))}
                {!procore && q && (
                  <PickerOption on={false} onPick={() => { set({ trade: { name: query.trim() } }); setOpenField(null); }} icon={<DsIcon name="plus" size={12} color={DS.meta} />}>
                    {`Use “${query.trim()}”`}
                  </PickerOption>
                )}
              </ChipPicker>
            ) : (
              <ChipPicker
                field="type" openField={openField} onToggle={toggle} has={!!draft.type}
                shown={draft.type ?? "Select type"}
                icon={<DsIcon name="list" size={13} color="currentColor" />}
                query={query} onQuery={setQuery} placeholder="Type…"
                empty={!!options && !(options.observationTypes ?? []).some(matches)}
                loading={!options && !optionsError}
                clearable={!!draft.type} onClear={() => { set({ type: null }); setOpenField(null); }}
              >
                {(options?.observationTypes ?? []).filter(matches).map((t) => (
                  <PickerOption key={t} on={draft.type === t} onPick={() => { set({ type: t }); setOpenField(null); }}>
                    {t}
                  </PickerOption>
                ))}
              </ChipPicker>
            )}
          </div>
        </div>

        {/* footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px 14px", borderTop: "1px solid rgba(10,10,10,0.08)" }}>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            hidden
            onChange={(event) => { void addPhotos(event.target.files); event.target.value = ""; }}
          />
          <button
            type="button"
            className="rt-hover"
            title="Add photo"
            aria-label="Add photo"
            data-testid="new-record-add-photo"
            disabled={draft.photos.length >= MAX_PHOTOS}
            onClick={() => fileInput.current?.click()}
            style={{ flex: "none", width: 34, height: 34, borderRadius: 17, border: "1px solid rgba(10,10,10,0.12)", background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: RT.cell }}
          >
            <DsIcon name="camera" size={15} color="currentColor" />
          </button>
          {isPunch && procore && draft.assignee?.remoteId && (
            <Toggle testId="new-record-send" on={sendNow} label={`Send to ${firstName ?? "assignee"}`} onToggle={() => setSendNow((on) => !on)} />
          )}
          <span
            data-testid="new-record-note"
            title={error ?? syncNote}
            style={{ flex: 1, minWidth: 0, fontSize: 12, color: error ? DS.red : DS.meta, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {error ?? syncNote}
          </span>
          <Toggle testId="new-record-more" on={createMore} label="Create more" onToggle={() => setCreateMore((on) => !on)} />
          <button
            type="button"
            data-testid="new-record-create"
            disabled={!canCreate}
            onClick={() => void create()}
            style={{ flex: "none", height: 36, padding: "0 16px", borderRadius: 18, background: DS.ink, color: RT.pane, border: "none", display: "inline-flex", alignItems: "center", font: "inherit", fontSize: 13, fontWeight: 700, cursor: canCreate ? "pointer" : "default", opacity: canCreate ? 1 : 0.45, whiteSpace: "nowrap" }}
          >
            {busy ? "Creating…" : isPunch ? "Create punch item" : "Create observation"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const CHIP: CSSProperties = {
  flex: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 30,
  maxWidth: 240,
  padding: "0 11px 0 9px",
  borderRadius: 15,
  border: "1px solid rgba(10,10,10,0.12)",
  background: RT.pane,
  font: "inherit",
  fontSize: 13,
  fontWeight: 500,
  color: DS.ink,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

export const CLEAR_ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  width: "100%",
  height: 32,
  padding: "0 10px",
  marginTop: 4,
  borderRadius: 7,
  border: "none",
  borderTop: "1px solid rgba(10,10,10,0.06)",
  background: "transparent",
  font: "inherit",
  fontSize: 12.5,
  color: DS.meta,
  cursor: "pointer",
};

function ChipPicker({
  field,
  openField,
  onToggle,
  has,
  shown,
  icon,
  children,
  query,
  onQuery,
  placeholder,
  empty,
  loading,
  note,
  clearable,
  onClear,
}: {
  field: Field;
  openField: Field | null;
  onToggle: (field: Field) => void;
  has: boolean;
  shown: string;
  icon: ReactNode;
  children: ReactNode;
  query: string;
  onQuery: (next: string) => void;
  placeholder: string;
  empty: boolean;
  loading?: boolean;
  note?: string;
  clearable?: boolean;
  onClear?: () => void;
}) {
  const open = openField === field;
  return (
    <span style={{ position: "relative", flex: "none" }}>
      <button
        type="button"
        className="rt-hover"
        data-testid={`new-record-${field}`}
        aria-expanded={open}
        onClick={(event) => { event.stopPropagation(); onToggle(field); }}
        style={{ ...CHIP, borderColor: open ? DS.ink : "rgba(10,10,10,0.12)", background: has ? RT.hover : RT.pane, color: has ? DS.ink : DS.body2, cursor: "pointer" }}
      >
        {icon}
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{shown}</span>
      </button>
      {open && (
        <PickerMenu query={query} onQuery={onQuery} placeholder={placeholder} empty={empty} loading={loading} note={note} clearable={clearable} onClear={onClear}>
          {children}
        </PickerMenu>
      )}
    </span>
  );
}

export function PickerMenu({ children, query, onQuery, placeholder, empty, loading, note, clearable, onClear }: {
  children: ReactNode;
  query: string;
  onQuery: (next: string) => void;
  placeholder: string;
  empty: boolean;
  loading?: boolean;
  note?: string;
  clearable?: boolean;
  onClear?: () => void;
}) {
  return (
    <div
      className="rt-fade"
      data-testid="new-record-menu"
      onClick={(event) => event.stopPropagation()}
      style={{ position: "absolute", left: 0, top: 36, zIndex: 60, minWidth: 240, maxWidth: 340, background: RT.pane, border: "1px solid rgba(10,10,10,0.12)", borderRadius: 10, boxShadow: RT.shadowPopover, display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 38, padding: "0 12px", borderBottom: "1px solid rgba(10,10,10,0.08)" }}>
        <DsIcon name="search" size={13} color={DS.meta} />
        <input
          className="rt-input"
          value={query}
          onInput={(event) => onQuery(event.currentTarget.value)}
          placeholder={placeholder}
          aria-label={placeholder.replace("…", "")}
          autoFocus
          style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 13, color: DS.ink }}
        />
      </div>
      <div className="rt-noscroll" style={{ maxHeight: 220, overflow: "auto", padding: 6 }}>
        {children}
        {loading && <span style={{ display: "block", padding: 10, fontSize: 12.5, color: RT.count }}>Loading…</span>}
        {!loading && empty && <span style={{ display: "block", padding: 10, fontSize: 12.5, color: RT.count }}>{note ?? "No matches"}</span>}
        {clearable && onClear && (
          <button type="button" className="rt-hover" onClick={onClear} style={CLEAR_ROW}>
            <DsIcon name="close" size={11} color={DS.meta} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export function PickerOption({ on, onPick, icon, detail, children }: { on: boolean; onPick: () => void; icon?: ReactNode; detail?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className="rt-hover"
      data-testid="new-record-option"
      onClick={onPick}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, minHeight: 34, padding: "0 10px", borderRadius: 7, border: "none", background: on ? RT.hover : "transparent", font: "inherit", fontSize: 13, color: DS.ink, cursor: "pointer", whiteSpace: "nowrap", textAlign: "left" }}
    >
      {icon}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
      {detail && <span style={{ flex: "none", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", fontSize: 11.5, color: DS.meta }}>{detail}</span>}
      {on && <DsIcon name="check" size={12} color={DS.ink} />}
    </button>
  );
}

